import React, { useEffect, useState } from "react";
import { Button, Col, Row, Skeleton, Table } from "antd";
import * as XLSX from "xlsx";
import columns from "./modulecolumns/NYSColumns";
import eventColumns from "./modulecolumns/EventColumns";

import { Notify, openNotification } from '@seims/shared';

function Preview({ file, setProgressState, onFileSubmit, recordLimit, moduleName, columnHeaders }) {
  const [previewData, setPreviewData] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [fileContents, setFileContents] = useState({
    name: "",
    size: "",
    contentLength: "",
    previewLength: "",
  });

  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [disableUpload, setDisableUpload] = useState(false);

  useEffect(() => {
    onFileLoad();
    getColumnsByModule(moduleName);
  }, []);

  const getColumnsByModule = (moduleName) => {

    if (moduleName === "nys-programme") {
      setDataColumns(columns.NYSDataColumns);
    }
    else if (moduleName === "events-tracker") {
      setDataColumns(eventColumns.EventDataColumns);
    }
  }

  const validateFile = (fileData) => {
    let isValid = true;
    if (fileData.length === 0) {
      openNotification("File Validation", `${file.name} appears to be empty/corrupted, please re-upload`, Notify.ERROR);
      setProgressState(0);
      isValid = false;
      return isValid;
    }

    if (fileData.length > recordLimit) {
      openNotification("File Records Validation", `Uploaded file must have exactly ${recordLimit} rows, please re-upload`, Notify.ERROR);
      setProgressState(0);
      isValid = false;
      return isValid;
    }

    return isValid;
  };

  // Utility function to convert Excel serial date to 'YYYY-MM-DD'
  const convertExcelDate = (serial) => {
    const excelEpoch = new Date(1899, 11, 30); // Excel's starting point
    const date = new Date(excelEpoch.getTime() + serial * 86400000);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const onFileLoad = () => {
    const promise = new Promise((resolve, reject) => {
      setLoading(true);
      const fileReader = new FileReader();

      if (!file) return;

      fileReader.readAsArrayBuffer(file);
      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = XLSX.read(bufferArray, { type: "buffer" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        let data = [];
   
        const headerRange = XLSX.utils.decode_range(ws['!ref']).e.c;
        const headers = [];
        for (let i = 0; i <= headerRange; i++) {
          const cellAddress = XLSX.utils.encode_col(i) + '1';
          const cell = ws[cellAddress];
          headers.push(cell ? cell.v : null);
        }

        if (moduleName === "events-tracker") {
          if (headers[0] !== "Event Details") {
            if (headers.length !== columnHeaders.length) {
              const showList = true;
              let messages = "The uploaded file does not match the events template";
              openNotification(
                `File Header Validation (${file.name})`,
                `${messages}`,
                Notify.ERROR,
                showList
              );
              setProgressState(0);
              setLoading(false);
              return;
            }
          }
        }else {
          let colHeaders = [];
          columnHeaders.map((header) => {
            colHeaders.push(header.value);
          });

           if (headers.length !== colHeaders.length) {
            const showList = true;
            let messages = `The uploaded file does not match the ${moduleName} template`;
            openNotification(
              `File Header Validation (${file.name})`,
              `${messages}`,
              Notify.ERROR,
              showList
            );
            setProgressState(0);
            setLoading(false);
            return;
          }
        }
    
        if (moduleName === "events-tracker") {
          data = XLSX.utils.sheet_to_json(ws, { header: ["Header Group", "Header Name", "Value"] });
          data = data.map((row) => {
            if (row["Header Name"] === "Event Start Date" && !isNaN(row["Value"])) {
              return {
                ...row,
                Value: convertExcelDate(row["Value"])
              };
            }
            return row;
          });
          data = data.map((row) => {
            if (row["Header Name"] === "Event End Date" && !isNaN(row["Value"])) {
              return {
                ...row,
                Value: convertExcelDate(row["Value"])
              };
            }
            return row;
          });
        } else if (moduleName === "nys-programme") {
          data = XLSX.utils.sheet_to_json(ws);
        }
        else {
          data = XLSX.utils.sheet_to_json(ws);
        }
        
         const previewData = data.slice(0, 1000);
        const isValid = validateFile(previewData);

        if (!isValid) return;

        const { size, name } = file;
        const content = { ...fileContents };
        content.name = name;
        content.size = `${Math.round(size / 1024, 0)}KB`;
        content.contentLength = `${data.length} Rows`;
        content.previewLength = `${previewData.length} Rows`;

        setFileContents(content);

        let fileColums = Object.keys(data[0]);
        let colCount = 0;
        let nonMatchingCols = [];
        const moduleHeaders = columnHeaders.map(c => c.value.toString())
        if (columnHeaders && columnHeaders.length > 0) {

          fileColums.forEach((column) => {
            if (moduleName === "nys-programme") {
              if (moduleHeaders.filter((x) => x === column)) {
                colCount += 1;
              }

              let found = moduleHeaders.find((x) => x != column);
              nonMatchingCols.push(found && column);
            } else if (moduleName === "events-tracker") {
              if (moduleHeaders.filter((x) => x === column)) {
                colCount += 1;
              }
              let found = moduleHeaders.find((x) => x != column);
              nonMatchingCols.push(found && column);
            }
          });

          let itemsNotFoundInSource = null;
          if (moduleName === "nys-programme") {
            itemsNotFoundInSource = fileColums.filter(
              (sourceItem) => !moduleHeaders.includes(sourceItem)
            );
          }
          else if (moduleName === "events-tracker") {
            itemsNotFoundInSource = fileColums.filter(
              (sourceItem) => !moduleHeaders.includes(sourceItem)
            );
          }
          if (itemsNotFoundInSource.length > 0) {
            let messages = "";
            itemsNotFoundInSource.forEach((item, index) => {
              if (item != null) {
                if (index < itemsNotFoundInSource.length - 1) {
                  messages += `"${item}" column does not match the ${moduleName} template;`;
                }
                else {
                  messages += `"${item}" column does not match the ${moduleName} template`;
                }
              }
            });
            setDisableUpload(true);
            resolve(previewData);
            setPreviewData(previewData);
            setLoading(false);
            const showList = true;
            openNotification(
              `File Header Validation (${file.name})`,
              `${messages}`,
              Notify.ERROR,
              showList
            );
            return;
          }
          let finalCount = 0;
          if (moduleName === "nys-programme") {
            finalCount = columnHeaders.length - colCount;
          } else if (moduleName === "events-tracker") {
            finalCount = columnHeaders.length - colCount;
          }

          if (previewData.length > 0 && finalCount === 0) {
            resolve(previewData);
            setPreviewData(previewData);
            setLoading(false);
          } else {
            const showList = true;
            openNotification(
              "File Validation",
              `${file.name} is either empty or does not comform to ${moduleName} template; System expected 18 columns but received ${fileColums.length}`,
              Notify.ERROR,
              showList
            );
            setProgressState(0);
          }

          if (data.length > recordLimit) {
            openNotification(
              "File Length Validation",
              `${file.name} has ${content.contentLength} of data, system expected ${recordLimit} or less`,
              Notify.ERROR
            );
            setProgressState(0);
          }
        };
        FileReader.onerror = (error) => {
          reject(error);
          setLoading(true);
        };
      }


    });

    promise.then((dataItems) => {
      setPreviewData(dataItems.slice(0, 1000));
    });
  };

  const onUploadFile = () => {
    //Send request to the BE
    setProgressState(2);
    onFileSubmit(file);
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const onBack = () => {
    setProgressState(0);
  };

  const isDisabled = (disabled) => {
    if (loading) return true;
    if (disabled) return true;
    if (!disabled) return false;
  };

  return (
    <>
      {loading ? (
        <Skeleton active avatar />
      ) : (
        <Table
          dataSource={previewData}
          columns={dataColumns}
          loading={loading}
          rowKey={(record) =>
            record.IDNumber
          }
          bordered
          pagination={{ pageSize: pageSize }}
          size="middle"
          scroll={{ x: "1500px" }}
          sticky
          onChange={onRowChange}
        />
      )}

      {!loading && (
        <div style={{ fontSize: "12px" }}>
          <div>
            <b>File name:</b> {fileContents.name}
          </div>
          <div>
            <b>File size:</b> {fileContents.size}
          </div>
          <div>
            <b>Total number of records:</b> {fileContents.contentLength}
          </div>
          <div>
            <b>Number of records on Preview:</b> {fileContents.previewLength}
          </div>
        </div>
      )}

      <Row>
        <Col xs={24} className="right-align">
          <Button
            className="right-align-button"
            onClick={onBack}
            type="deafult"
            style={{ marginTop: "20px" }}
          >
            Back
          </Button>{" "}
          &nbsp;
          <Button
            className="right-align-button"
            onClick={onUploadFile}
            htmlType="submit"
            type="primary"
            style={{ marginTop: "20px" }}
            disabled={isDisabled(disableUpload)}
          >
            Upload
          </Button>
        </Col>
      </Row>
    </>
  );
}

export default Preview;
